import { useHistory } from "react-router-dom";
import React, { useContext, useState, useMemo } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { getIndexRoute, ResourceName } from "../../main/Routing";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { DatasetsFilterForm, DatasetsFilterbar } from "./DatasetsFilterBar";
import { DownloadDatasetMulti } from "../common/DownloadDataset/DownloadDataset";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import {
  DatasetFilters,
  Dataset,
  DatasetFiltersTranslator,
  DatasetSearchTermOptions,
  datasetsConstants,
} from "../../api/Datasets";
import { ExclusiveDropdown } from "../../common/buttons/ExclusiveDropdown/ExclusiveDropdown";
import { DatasetsBulkEditDropDownWrapper } from "./DatasetsBulkEditDropDown";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import styles from "../../common/tables/GenericVirtualizedTable/commons.module.css";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { SharedContentModal } from "../../common/helpers/EntityModals/SharedContentModal";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import { NotAvailable, NotSet } from "../../common/misc/UIconstants";
import { GenericBooleanStateProvider } from "../../common/modals/Modal/GenericModal";
import { GetPersons, TableArrayRenderer } from "../../common/misc/EntityRenders/EntityRenderer";
import { useUnpaginateEntities } from "../../common/forms/MultiEditForms/common/MultiEditUtils";
import { Button } from "../../common/buttons/Button/Button";
import { ExportCsvButton } from "../../common/tables/ExportCsvButton/ExportCsvButton";
import { useCustomTypesEntityTable } from "../../Customization/CustomTypes/generics/useCustomTypesEntityTable";
import { RenderParsingState } from "../common/HelperModules";
import SearchInputWithOptions from "../../common/forms/SearchInputWithOptions/SearchInputWithOptions";
import { renderCustomTypeColumn } from "../../Customization/CustomTypes/generics/CustomTypeRenderer";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
  UseITypedEntityTableProps,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { usePostMutation } from "../../api/BaseEntityApi";
import { customImportConstants, CustomImportResult } from "../../api/CustomImports";
import { showtoast } from "../../common/overlays/Toasts/showtoast";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityTableSoftDeletableButton } from "../../common/entity/entityComponents/EntityTableSoftDeletableButton";
import { EntityTableRestoreButton } from "../../common/entity/entityComponents/EntityTableRestoreButton";
import { maxDatasetsInViewer } from "../MultiViewer";
import { GenericEntityTableComplianceDropdown } from "../../Entities/GenericComponents/common/GenericEntityTable";
import { NestedDropdown } from "../../common/buttons/NestedDropdown/NestedDropdown";
import { ExportPdfButton } from "../../common/tables/ExportPdfButton/ExportPdfButton";
import { EntityOrderBy, InferSortByOptions, ISearchTermParametersDefaults } from "../../api/GenericTypes";
import { dataSourceConstants } from "../../api/DataSource";

export const switchDatasetsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<DatasetFilters>>[] | null | undefined
): SortState<InferSortByOptions<DatasetFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "CREATION_DATE":
        return {
          headerId: "default-creationDate",
          sortDirection: sort.sortDirection,
          property: sort.property,
        };
      case "PARSING_STATE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-parsingState",
          property: sort.property,
        };
      case "FORMAT_ID":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-format",
          property: sort.property,
        };
      case "SOURCE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-source",
          property: sort.property,
        };
      default:
        return { headerId: "default-creationDate", sortDirection: "DESC", property: "CREATION_DATE" };
    }
  });
};

export const defaultDatasetsFilters: DatasetFilters = {
  sortBy: [
    {
      property: "CREATION_DATE",
      sortDirection: "DESC",
    },
  ],
  ...ISearchTermParametersDefaults,
  searchTermIncludeParameters: false,
  searchTermIncludePaths: false,
};

export const useDatasetsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"datasets">) => {
  const { session } = useContext(SessionContext);

  const defaults: ColumnsSettings<Dataset> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-automaticName": {
        pos: 2,
        active: false,
        header: fieldLabels.automaticName,
        property: "automaticName",
      },
      "default-customType": { pos: 3, active: true, header: fieldLabels.customType, property: "customType" },
      "default-creationDate": { pos: 4, active: true, header: fieldLabels.creationDate, property: "creationDate" },
      "default-projects": { pos: 5, active: true, header: fieldLabels.projects, property: "projects" },
      "default-owner": { pos: 6, active: true, header: fieldLabels.owner, property: "owner" },
      "default-enteredOn": { pos: 7, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 8, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 9, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 10, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
      "default-format": { pos: 11, active: false, header: fieldLabels.format, property: "format" },
      "default-sourcepath": { pos: 12, active: false, header: fieldLabels.path, property: "path" },
      "default-uid": { pos: 13, active: false, header: fieldLabels.uid, property: "uid" },
      ...((!!session?.permissions.maintenance ||
        !!session?.permissions.view_admin_pages ||
        !!session?.features.activate_maintenance) && {
        "default-parsingState": { pos: 14, active: false, header: fieldLabels.parsingState, property: "parsingState" },
      }),
      "default-source": { pos: 15, active: true, header: fieldLabels.source, property: "source" },
    }),
    [
      session?.features.activate_maintenance,
      session?.permissions.maintenance,
      session?.permissions.view_admin_pages,
      fieldLabels,
    ]
  ); // Column defaults

  const tabStoreDefaults: TableTabsDict<Dataset, DatasetFilters, DatasetsFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultDatasetsFilters,
          sidebarFilters: { includeUnclaimed: true, includeSoftDeleted: false, includeViewableEntities: false },
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      mydata: {
        tabId: "mydata",
        type: "fixed",
        label: `My ${datasetsConstants.entityPlural}`,
        title: `My ${datasetsConstants.entityPlural}`,
        icon: "user",
        align: "left",
        xPos: 1,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultDatasetsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { ownerIds: session?.person.id ? [session.person.id] : null },
          sidebarFilters: {},
        },
      },
      unclaimed: {
        tabId: "unclaimed",
        type: "fixed",
        label: "Unclaimed",
        title: "Unclaimed",
        icon: "flag-off",
        align: "left",
        xPos: 2,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultDatasetsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {
            "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
            "default-name": { pos: 2, active: true, header: fieldLabels.name, property: "name" },
            "default-sourcepath": { pos: 3, active: true, header: fieldLabels.path, property: "path" },
            "default-creationDate": {
              pos: 7,
              active: true,
              header: fieldLabels.creationDate,
              property: "creationDate",
            },
            "default-projects": { pos: 8, active: true, header: fieldLabels.projects, property: "projects" },
            "default-enteredOn": { pos: 11, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
            "default-enteredBy": { pos: 12, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
            "default-modifiedOn": {
              pos: 13,
              active: false,
              header: fieldLabels.modifiedOn,
              property: "modifiedOn",
            },
            "default-modifiedBy": {
              pos: 14,
              active: false,
              header: fieldLabels.modifiedBy,
              property: "modifiedBy",
            },
            "default-customType": { pos: 15, active: false, header: fieldLabels.customType, property: "customType" },
            "default-format": { pos: 20, active: false, header: fieldLabels.format, property: "format" },
            // "default-relpath": {
            //   pos: 21,
            //   active: false,
            //   header: fieldLabels.sourceRelativeDirectory,
            //   property: "sourceRelativeDirectory",
            // },
            "default-uid": { pos: 22, active: false, header: fieldLabels.uid, property: "uid" },
            ...((!!session?.permissions.maintenance ||
              !!session?.permissions.view_admin_pages ||
              !!session?.features.activate_maintenance) && {
              "default-parsingState": {
                pos: 23,
                active: false,
                header: fieldLabels.parsingState,
                property: "parsingState",
              },
            }),
            // "legacy-brukerPulseProgram": {
            //   pos: 24,
            //   active: false,
            //   header: "Bruker Pulse Program",
            //   property: "legacyFields.brukerPulseProgram",
            // },
          },
          columnWidths: {},
          filters: {
            isClaimed: false,
          },
          sidebarFilters: { includeUnclaimed: true },
        },
      },
      trash: {
        tabId: "trash",
        type: "fixed",
        label: "Trash",
        title: "Trash",
        icon: "trash",
        align: "left",
        xPos: 3,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultDatasetsFilters,
          sidebarFilters: { includeUnclaimed: true },
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { isSoftDeleted: true },
          sidebarFilters: {},
        },
      },
    }),
    [
      fieldLabels.creationDate,
      fieldLabels.enteredBy,
      fieldLabels.enteredOn,
      fieldLabels.customType,
      fieldLabels.format,
      fieldLabels.id,
      fieldLabels.modifiedBy,
      fieldLabels.modifiedOn,
      fieldLabels.name,
      fieldLabels.parsingState,
      fieldLabels.path,
      fieldLabels.projects,
      // fieldLabels.sourceRelativeDirectory,
      fieldLabels.uid,
      session?.features.activate_maintenance,
      session?.permissions.maintenance,
      session?.permissions.view_admin_pages,
      session?.person.id,
    ]
  );

  return { defaults, tabStoreDefaults };
};

export const useDatasetsTableColumns = ({
  entityConstants,
  fieldLabels,
  defaults,
  filters,
  sort,
  setSort,
}: UseITypedEntityTableProps<"datasets">) => {
  // Custom Fields
  const { columnSettings, customFieldHeaders } = useCustomTypesEntityTable({
    defaultColumnSettings: defaults,
    entityType: "Dataset",
    customTypeIds: filters?.customTypeIds ?? undefined,
    sort,
    setSort,
  });

  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<Dataset> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => (
          <span style={{ color: "var(--gray-400)" }}>
            <samp>{row.id}</samp>
          </span>
        ),
        width: 135,
        minWidth: 135,
        align: "right",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        id: "default-name",
        Header: fieldLabels.name,
        accessor: (row) => (
          <div className={`${styles.container_ellipsis} gap-5`}>
            <div className={`${styles.container_ellipsis}`}>
              <span className={styles.label_highlight}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name || NotAvailable}
              </span>
            </div>
            {!row.claimed && (
              <div>
                <label className="label label-soft-secondary" title={"Unclaimed"}>
                  <LucideIcon name="flag-off" />
                </label>
              </div>
            )}
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        id: "default-automaticName",
        Header: fieldLabels.automaticName,
        accessor: (row) =>
          !!row.automaticName ? (
            <div className={`${styles.container_ellipsis} gap-5`}>
              <div className={`${styles.container_ellipsis}`}>
                <span className={styles.label_highlight}>
                  <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.automaticName}
                </span>
              </div>
            </div>
          ) : (
            NotSet
          ),
        minWidth: 150,
        width: 250,
        align: "left",
      },
      {
        id: "default-creationDate",
        Header: fieldLabels.creationDate,
        accessor: (row) => <DateTimeRenderer date={row.creationDate} includeElapsed={false} />,
        width: 175,
        minWidth: 175,
        align: "left",
        ...getAsyncSortingFn("CREATION_DATE", sort, setSort),
      },
      {
        id: "default-owner",
        Header: fieldLabels.owner,
        accessor: (row) => (row.owner ? <GetPersons persons={[row.owner]} createLinks={false} /> : NotSet),
        width: 200,
        align: "left",
        ...getAsyncSortingFn("OWNER", sort, setSort),
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
      {
        id: "default-projects",
        Header: fieldLabels.projects,
        accessor: (row) => <TableArrayRenderer values={row.projects}>{(value) => value.name}</TableArrayRenderer>,
        width: 200,
        align: "left",
      },
      {
        id: "default-format",
        Header: fieldLabels.format,
        accessor: (row) => (
          <div className={styles.container_ellipsis}>
            <span>{row.format?.name ?? NotSet}</span>
          </div>
        ),
        width: 200,
        align: "left",
        ...getAsyncSortingFn("FORMAT_ID", sort, setSort),
      },
      {
        id: "default-sourcepath",
        Header: fieldLabels.path,
        accessor: (row) => (
          <div className={styles.container_ellipsis}>
            <span>{row.path || NotSet}</span>
          </div>
        ),
        width: 200,
        align: "left",
      },
      // {
      //   id: "default-relpath",
      //   Header: fieldLabels.sourceRelativeDirectory,
      //   accessor: (row) => (
      //     <div className={styles.container_ellipsis}>
      //       <span>{row.sourceRelativeDirectory || NotSet}</span>
      //     </div>
      //   ),
      //   width: 200,
      //   align: "left",
      // },
      {
        id: "default-uid",
        Header: fieldLabels.uid,
        accessor: (row) => (
          <div className={styles.container_ellipsis}>
            <span>{row.uid}</span>
          </div>
        ),
        width: 200,
        align: "left",
      },
      {
        id: "default-parsingState",
        Header: fieldLabels.parsingState,
        accessor: (row) => (
          <div className={styles.container_ellipsis}>
            <span>
              <RenderParsingState state={row.parsingState} />
            </span>
          </div>
        ),
        width: 200,
        align: "left",
      },
      {
        id: "default-source",
        Header: fieldLabels.source,
        accessor: (row) => (
          <div className={styles.container_ellipsis}>
            {row.source?.name ? (
              <span>
                <LucideIcon name={dataSourceConstants.icon} color={"var(--primary)"} style={{ flexShrink: 0 }} />{" "}
                {row.source.name}
              </span>
            ) : (
              NotAvailable
            )}
          </div>
        ),
        ...getAsyncSortingFn("SOURCE", sort, setSort),
        width: 200,
        align: "left",
      },
      // {
      //   id: "legacy-brukerPulseProgram",
      //   Header: "Bruker Pulse Program",
      //   accessor: (row) => (
      //     <div className={styles.container_ellipsis}>
      //       <span>{row.legacyFields?.brukerPulseProgram ?? NotSet}</span>
      //     </div>
      //   ),
      //   width: 200,
      //   align: "left",
      // },
      // {
      //   id: "legacy-notes",
      //   Header: "Bruker Title",
      //   accessor: (row) => (
      //     <div className={styles.container_ellipsis}>
      //       <span>{row.legacyFields?.brukerTitle ?? NotSet}</span>
      //     </div>
      //   ),
      //   width: 200,
      //   align: "left",
      // },
    ];

    // Add custom field headers
    headers.push(renderCustomTypeColumn(entityConstants, sort, setSort));
    headers = headers.concat(customFieldHeaders);
    return headers;
  }, [
    fieldLabels.id,
    fieldLabels.name,
    fieldLabels.automaticName,
    fieldLabels.creationDate,
    fieldLabels.owner,
    fieldLabels.enteredOn,
    fieldLabels.enteredBy,
    fieldLabels.modifiedOn,
    fieldLabels.modifiedBy,
    fieldLabels.projects,
    fieldLabels.format,
    fieldLabels.path,
    fieldLabels.uid,
    fieldLabels.parsingState,
    fieldLabels.source,
    sort,
    setSort,
    entityConstants,
    customFieldHeaders,
  ]);

  return { columns, columnSettings };
};

export const DatasetsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"datasets">) => {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const { session, api, route } = useContext(SessionContext);
  const { unpaginateEntities } = useUnpaginateEntities({ resource: entityConstants.resource });
  const {
    bulkDeleteMutationAsync,
    bulkRestoreMutationAsync,
    bulkEditMutationAsync,
    isLoadingBulkEditMutation,
    isLoadingRestoreMutation,
    isLoadingBulkDeleteMutation,
  } = entityApi;

  const { mutateAsync: bulkApplyCustomImportMutationAsync, isLoading: isLoadingBulkApplyCustomImportMutation } =
    usePostMutation<CustomImportResult[], { datasetIds: number[] }>({
      resource: "custom_imports/datasets" as ResourceName,
    });

  const { defaults, tabStoreDefaults } = useDatasetsTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<Dataset>();

  const {
    filters,
    forcedFilters,
    sidebarFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<Dataset, DatasetFilters, DatasetsFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, onTabChange, sort, setSort, searchValue, setSearchValue, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchDatasetsDefaultSortState,
      dispatchTabStore,
    });

  const { columns, columnSettings } = useDatasetsTableColumns({
    fieldLabels,
    entityConstants,
    defaults,
    filters,
    sort,
    setSort,
  });

  const memoizedFilters = useMemo(
    () => ({
      ...filters,
      ...forcedFilters,
      ...((!!session?.permissions.maintenance ||
        !!session?.permissions.view_admin_pages ||
        !!session?.features.activate_maintenance) && { includeParsingState: true }),
    }),
    [
      filters,
      forcedFilters,
      session?.features.activate_maintenance,
      session?.permissions.maintenance,
      session?.permissions.view_admin_pages,
    ]
  );

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <button
            className="btn btn-primary"
            onClick={() => history.push(routes.getAddRoute)}
            disabled={!permissions.canCreate}
          >
            <LucideIcon name="upload" /> Upload {entityConstants.entitySingular}
          </button>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <DatasetsFilterbar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={{ ...filters, includeParsingState: true }}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<Dataset, DatasetFilters>
                filters={filters}
                translatorConsts={DatasetFiltersTranslator}
                excludeFilters={{
                  includeParsingState: () => false,
                  includeSoftDeleted: (value) => value === true,
                  includeViewableEntities: (value) => value === true,
                  isSoftDeleted: () => false,
                  ...(currentTab === "claimed" && { isClaimed: () => false }),
                  includeUnclaimed: (value) => false,
                  searchTermIncludeIds: () => false,
                  searchTermIncludeNames: () => false,
                  searchTermIncludeParameters: () => false,
                  searchTermIncludePaths: () => false,
                  searchTermIncludeUuids: () => false,
                  searchTermIncludeCustomFieldValues: () => false,
                }}
                style={{ margin: "auto 0" }}
              />
              {/* <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" /> */}
              <SearchInputWithOptions
                searchTermOptions={DatasetSearchTermOptions}
                filters={filters}
                setFilters={async (newFilters) => {
                  await dispatchTabStore({
                    type: "setTab",
                    options: { keepPrevious: true },
                    payload: { settings: { filters: { searchTerm: debouncedSearchValue, ...newFilters } } },
                  });
                }}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
              />
              <div
                className="flex row-nowrap align-center gap-5"
                style={{ height: "min-content", marginBottom: "auto" }}
              >
                {currentTab !== "trash" && (
                  <GenericBooleanStateProvider>
                    {({ isTrue: showModal, setIsTrue: setShowModal }) => (
                      <ExclusiveDropdown
                        show={showModal}
                        setShow={setShowModal}
                        disabled={
                          selection.size === 0 ||
                          (session?.features.development_mode ? false : selection.size > 1000) ||
                          !permissions.canBulkEdit(selectionPermissions)
                        }
                        title={`${
                          !permissions.canBulkEdit(selectionPermissions)
                            ? `Insufficient permissions to edit selected ${entityConstants.entityPlural}`
                            : selection.size > 1000
                              ? `Editing is only allowed for max. 1000 ${entityConstants.entityPlural} at once`
                              : `Edit`
                        }`}
                        btnCls={"btn btn-default"}
                        btnLabel={`${currentTab === "unclaimed" ? "Edit / Claim" : "Edit"}`}
                        icon={<LucideIcon name="square-pen" />}
                      >
                        <DatasetsBulkEditDropDownWrapper
                          selection={selection}
                          onSuccess={() => {
                            functionRef.current?.resetSelection();
                            setShowModal(false);
                          }}
                          onClose={() => {
                            setShowModal(false);
                          }}
                          filters={filters}
                          entityConstants={entityConstants}
                        />
                      </ExclusiveDropdown>
                    )}
                  </GenericBooleanStateProvider>
                )}
                {currentTab !== "trash" && (
                  <button
                    className="btn btn-default"
                    title="Show selected datasets in viewer"
                    disabled={selection.size === 0 || selection.size > maxDatasetsInViewer}
                    onClick={() => {
                      const searchParams = new URLSearchParams();
                      searchParams.set("ids", Array.from(selection).join(","));
                      history.push({
                        pathname: route(`${getIndexRoute(entityConstants.frontendIndexRoute)}/overlay`),
                        search: searchParams.toString(),
                      });
                    }}
                  >
                    <LucideIcon name="chart-spline" /> Open in viewer
                  </button>
                )}
                <DownloadDatasetMulti
                  api={api}
                  disabled={selection.size === 0}
                  filters={{ ids: Array.from(selection), includeSoftDeleted: true }}
                  title={`Download the selected ${entityConstants.entityPlural}`}
                />
                <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                  {currentTab !== "unclaimed" && (
                    <li>
                      <Button
                        className="btn btn-sm btn-ghost-danger"
                        title={`${
                          !permissions.canBulkEdit(selectionPermissions)
                            ? `Insufficient permissions to unclaim selected ${entityConstants.entityPlural}`
                            : selection.size === 0
                              ? `Select ${entityConstants.entitySingular} to unclaim`
                              : selection.size === 1
                                ? `Unclaim the selected ${entityConstants.entitySingular}`
                                : `Unclaim the selected ${entityConstants.entityPlural}`
                        }`}
                        disabled={selection.size === 0 || !permissions.canBulkEdit(selectionPermissions)}
                        onClick={async () => {
                          const data = await unpaginateEntities<Dataset>(Array.from(selection));
                          await bulkEditMutationAsync({
                            body: Object.values(data).map((e) => ({ ...e, claimed: false })),
                          }).catch(() => {});
                        }}
                        loading={isLoadingBulkEditMutation}
                      >
                        <LucideIcon name="circle-x" /> Unclaim
                      </Button>
                    </li>
                  )}
                  {currentTab !== "trash" && (
                    <>
                      <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                        <li>
                          <SharedContentModal
                            datasetIds={Array.from(selection)}
                            disabled={selection.size === 0 || !session?.permissions.add_bundles}
                          />
                        </li>
                        <li>
                          <ExportCsvButton
                            entityConstants={entityConstants}
                            columnSettings={columnSetting}
                            filters={{
                              ids: selection.size > 0 ? Array.from(selection) : undefined,
                              ...memoizedFilters,
                              searchTerm: debouncedSearchValue,
                            }}
                            disabled={selection.size === 0}
                          />
                        </li>
                        <li>
                          <ExportPdfButton
                            entityConstants={entityConstants}
                            filters={{
                              ids: selection.size > 0 ? Array.from(selection) : undefined,
                              ...memoizedFilters,
                              searchTerm: debouncedSearchValue,
                            }}
                            disabled={selection.size === 0}
                          />
                        </li>
                      </NestedDropdown>
                    </>
                  )}

                  {/* Locking & Signing */}
                  <GenericEntityTableComplianceDropdown
                    entityTypeId={entityTypeId}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    tableFunctionRef={functionRef}
                    currentTab={currentTab}
                    showSignButton
                  />
                  {session?.permissions.logsadmin && (
                    <li>
                      <Button
                        className="btn btn-sm btn-ghost-success"
                        title={`Bulk apply ${customImportConstants.entitySingular}`}
                        onClick={async () => {
                          await bulkApplyCustomImportMutationAsync(
                            { body: { datasetIds: Array.from(selection) } },
                            {
                              onSuccess: (data) => {
                                showtoast(
                                  "success",
                                  `Successfully applied ${customImportConstants.entityPlural} to ${
                                    data.length ?? "N/A"
                                  }/${selection.size} datasets`
                                );
                                console.log("Bulk apply custom import result: ", data);
                              },
                            }
                          ).catch(() => {});
                        }}
                        loading={isLoadingBulkApplyCustomImportMutation}
                        disabled={selection.size === 0}
                      >
                        <LucideIcon name={customImportConstants.icon} /> Apply {customImportConstants.entitySingular}
                      </Button>
                    </li>
                  )}
                  <li className="divider" />
                  {/* Trash */}
                  {currentTab === "trash" && (
                    <li>
                      <EntityTableRestoreButton
                        entityConstants={entityConstants}
                        selection={selection}
                        permissions={permissions}
                        selectionPermissions={selectionPermissions}
                        onClick={async () =>
                          await bulkRestoreMutationAsync({ ids: Array.from(selection) }).catch(() => {})
                        }
                        loading={isLoadingRestoreMutation}
                      />
                    </li>
                  )}
                  <li>
                    <EntityTableSoftDeletableButton
                      currentTab={currentTab}
                      entityConstants={entityConstants}
                      selection={selection}
                      permissions={permissions}
                      selectionPermissions={selectionPermissions}
                      onClick={() => setShowModal(true)}
                    />
                  </li>
                </MoreDropdown>
              </div>
              <AlertModal
                type={`${currentTab === "trash" ? "danger" : "warning"}`}
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select dataset to ${currentTab === "trash" ? "delete" : "trash"}`
                    : selection.size === 1
                      ? `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entitySingular}?`
                      : `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entityPlural}?`
                }`}
                description={`${
                  currentTab === "trash"
                    ? `Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`
                    : `Proceeding will move the selected ${entityConstants.entityPlural} into trash.`
                }`}
                proceedLabel={`${currentTab === "trash" ? "Delete" : "Trash"}`}
                onProceed={async () => {
                  await bulkDeleteMutationAsync({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.entityPlural,
                    params: { deletePermanently: currentTab === "trash" },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<Dataset, DatasetFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={columnSettings}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
